import TimeAxis from "../TimeAxis";
import ConnectedTimeline, {
  allTimelinesLoadedAtom,
} from "../ConnectedTimeline";
import { useSettings } from "../../../../contexts/SettingsManager/SettingsManager";
import {
  useGetPageFromPath,
  useDelayFirstLoad,
  useEventListener,
} from "../../../../utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import useContainerDimensions from "utils/useContainerDimensions";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { useHighlightOverlappingSegments, useIsDarkMode } from "store/atoms";
import {
  useDataSourceGroups,
  useHighlightedTags,
} from "../../HubletView/SingleTimeline/singleTimelineStore";
import { useEffect, useRef, useState, useTransition } from "react";
import {
  BREAKPOINT_DESKTOP_XL,
  ROW_HEIGHT,
  ROW_PADDING,
} from "constants/constants";
import { useMount } from "react-use";
import { Process } from "types/types";
import { useMediaQuery } from "@mui/material";
import { useTrendChartBarRangesForBarstacks } from "utils/customHooks/trendChartHooks";
import { useIsTakingSnapshot } from "pages/SnapshotsPage/SnapshotElementButton";
import { useAtom } from "jotai";
import { isEqual } from "lodash";

const MIN_PROCESSES_FOR_VIRTUAL_TIMELINES = 6;

export const TimeAxisColumn = ({
  processes,
  isTrendsPage,
}: {
  processes: Process[];
  isTrendsPage: boolean;
}) => {
  const { timeZoneLocaleString, shouldUseCustomTimeZone } = useSettings();
  const ckey = useCustomerKey();
  // if trends page, disable the metric columns

  const [ref, dimensions] = useContainerDimensions();
  const isPrint = useMediaQuery("print");
  let width = dimensions.width || 0;
  const trendChartBarsRanges = useTrendChartBarRangesForBarstacks();
  const isManyBars = trendChartBarsRanges.length > 12;
  if (isPrint) {
    if (isTrendsPage && isManyBars) {
      width = 890;
    } else {
      width = 640;
    }
  }
  const { isGridLayoutPage, isHubletPage, isTimelinePage } =
    useGetPageFromPath();

  const isFirstLoadComplete =
    useDelayFirstLoad({ delayMs: 5 * 1000 }) || isPrint;

  const { selectedDataSourceGroups } = useDataSourceGroups();
  const [highlightedTags] = useHighlightedTags();
  const [highlightOverlappingSegments] = useHighlightOverlappingSegments();
  const areAnyTagsHighlighted = highlightedTags.length > 0;
  const showTagHighlights =
    areAnyTagsHighlighted || highlightOverlappingSegments;

  const [isPending, startTransition] = useTransition();
  // reset the tooltip/axis transforms AFTER the data is fetched (so we don't see it snap back to its initial position before loading new data)
  useEffect(() => {
    if (!isPending && !isPrint) {
      const timeAxisSvg = document.querySelector("#timeAxisSvg");

      timeAxisSvg?.setAttribute("transform", `translate(0, 0)`);
      // reset the timeline & tooltip transforms
      const tooltip = document.querySelector<HTMLDivElement>(
        `[data-component-name="TimelineTooltip"]`
      );
      if (tooltip && window.innerWidth < BREAKPOINT_DESKTOP_XL) {
        tooltip.style.transform = `translate(${0}px, 0)`;
      }
    }
  }, [isPending, isPrint]);

  // timelines start hidden
  const [visibleTimelinesIdxs, setVisibleTimelinesInViewport] = useState<
    boolean[]
  >(
    processes.map(
      (_, idx) => isPrint || idx <= MIN_PROCESSES_FOR_VIRTUAL_TIMELINES
    )
  );
  const [isTakingSnapshot] = useIsTakingSnapshot();
  // while taking snapshot, we need all timelines visible
  const [allTimelinesLoaded, setAllTimelinesLoaded] = useAtom(
    allTimelinesLoadedAtom
  );
  // while taking snapshot, show all timelines, and ensure allTimelinesLoaded has a key for each process
  useEffect(() => {
    if (isTakingSnapshot) {
      const nextAllTimelinesLoaded = { ...allTimelinesLoaded };
      // make sure allTimelinesLoaded has a key for each process so we can detect when all are done loading
      processes.forEach((process, idx) => {
        if (!allTimelinesLoaded[idx]) {
          nextAllTimelinesLoaded[idx] = false;
        }
      });
      if (!isEqual(allTimelinesLoaded, nextAllTimelinesLoaded)) {
        setAllTimelinesLoaded(nextAllTimelinesLoaded);
      }
      // show all timelines while taking snapshot (this gets reset on "scrollend" listener below)
      setVisibleTimelinesInViewport(processes.map(() => true));
    }
  }, [isTakingSnapshot, processes, allTimelinesLoaded, setAllTimelinesLoaded]);

  // some devices/browsers can't detect "scrollend" event -- in this case, don't virtualize timeline
  const canHandleScrollEndRef = useRef(false);

  // - track the viewport to calculate the itersection between timelines and the viewport
  // - don't render timelines that aren't visible
  // - after scroll end
  useEventListener("scrollend", e => {
    canHandleScrollEndRef.current = true;
    if (
      isTakingSnapshot || // don't hide timelines while taking a snapshot
      isPrint ||
      processes.length < MIN_PROCESSES_FOR_VIRTUAL_TIMELINES
    ) {
      return;
    }
    const areDataSourceGroupTimelinesVisible =
      selectedDataSourceGroups.length > 0;
    const visibleIdxs = areDataSourceGroupTimelinesVisible
      ? processes.map(() => true) // if a raven internal user is inspecting data source groups, don't worry about performance, just show all timelines
      : recalculateVisibleRows(processes);
    startTransition(() => {
      setVisibleTimelinesInViewport(visibleIdxs);
    });
  });

  // ensure the correct timelines are visible after the first load
  useMount(() => {
    setTimeout(() => {
      if (processes.length < MIN_PROCESSES_FOR_VIRTUAL_TIMELINES || isPrint) {
        return;
      }
      const visibleIdxs = recalculateVisibleRows(processes);
      startTransition(() => {
        setVisibleTimelinesInViewport(visibleIdxs);
      });
    }, 5 * 1000);

    // bugfix for slower devices / that can't handle "scrollend" event: after a longer timeout, if visible timelines are empty, set them all to visible
    // (also applies if user hasn't scrolled within the allotted time)
    setTimeout(() => {
      if (!canHandleScrollEndRef.current) {
        setVisibleTimelinesInViewport(processes.map(() => true));
      }
    }, 10 * 1000);
  });
  const fromSnapshots = window.location.pathname.includes("fromSnapshots");
  const [isDarkMode] = useIsDarkMode();
  return (
    <div
      className={`timeAxisColumn ${isPending && !fromSnapshots && !isTakingSnapshot ? "pending" : ""}`}
      data-server-names={[SERVER_NAMES.askrSegments]}
      ref={ref}
    >
      <TimeAxis
        width={width}
        isDarkMode={isDarkMode}
        isTakingSnapshot={isTakingSnapshot}
        startTransition={startTransition}
        zoomable={!isTrendsPage}
        startSliding={false}
        customerKey={ckey}
        timeZoneLocaleString={timeZoneLocaleString}
        shouldUseCustomTimeZone={shouldUseCustomTimeZone}
        showTagHighlights={showTagHighlights}
        {...{
          isGridLayoutPage,
          isHubletPage,
          isMultipleTimelinesPage: isTimelinePage || isTrendsPage,
        }}
      >
        {processes.map((process, idx) => {
          const shouldHide = !visibleTimelinesIdxs[idx];

          if (shouldHide) {
            return (
              <g
                key={`${process.id}--${process.name}`}
                transform={`translate(0, ${
                  idx * (ROW_HEIGHT + ROW_PADDING) +
                  ROW_HEIGHT * 0.5 -
                  2 * ROW_PADDING
                })`}
                className="timelineSkeletonLoader"
                {...({
                  process,
                } as any)}
              ></g>
            );
          }
          return (
            <ConnectedTimeline
              key={`${process.id}--${process.name}`}
              process={process}
              timelineIdx={idx}
              isSkeletonLoader={!isFirstLoadComplete && processes.length > 5}
              width={width}
            />
          );
        })}
      </TimeAxis>
    </div>
  );
};
function recalculateVisibleRows(processes: Process[]) {
  return processes.map((process, idx) => {
    const timeAxisSvg = document.querySelector(`#timeAxisSvg`);
    const timeAxisSvgRect = timeAxisSvg?.getBoundingClientRect();
    if (!timeAxisSvgRect) {
      return false;
    }
    const rowHeight = ROW_HEIGHT + 2 * ROW_PADDING;
    const thisRowOffsetTop = rowHeight * idx;
    // if the row is vertically outside the viewport, don't render it
    const rowTop = timeAxisSvgRect.top + thisRowOffsetTop;
    const isAboveTop = rowTop < -rowHeight;
    const isBelowBottom = rowTop > window.scrollY + window.innerHeight;

    // row is visible if its top is > 0 and its bottom is < window.scrollY
    const isRowVisible = !isAboveTop && !isBelowBottom;

    return isRowVisible;
  });
}
